import { Button, Drawer, DrawerProps, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { cx, css } from '@emotion/css';
import { BaseContextProvider, ItemContextProvider, SearchProvider } from './Context';
import { SearchComp } from './SearchComp';
import { ListView } from './components/ListView';
import { ListPagination } from './components/Pagination';
import { ItemV5, SearchProps } from './types';
import {
  SpuClassListSelect,
  RefProps,
} from '@knowledge/knowledges/singleManage/SpuClassListSelect';
import { useRef, useState } from 'react';
import { countSku } from '@knowledge/common/helpers/sku';
import { getMultiLangValue } from '@knowledge/common/hooks/useKnowledgeName';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useUpdateSingleClass } from '@knowledge/knowledges/singleManage/connect';
import { useQueryClient } from '@tanstack/react-query';
import { SupplementalKnowledgeAiReplyKnowledge } from 'services/api/supplementKnowledge';

export type ItemSelectV5DrawerSelfProps = {
  renderTitleExtra?: (items: ItemV5[], resetItems: () => void) => React.ReactNode;
  renderFooter?: (items: ItemV5[], resetItems: () => void) => React.ReactNode;
  renderExtra?: (items: ItemV5[], resetItems: () => void) => React.ReactNode;
};
interface ItemSelectV5DrawerProps extends ItemSelectV5DrawerSelfProps, DrawerProps {
  showSpuClassSelect?: boolean;
  showSameSellerSkuTag?: boolean;
  defaultParams?: Partial<SearchProps>;
  bindItem?: SupplementalKnowledgeAiReplyKnowledge['bindItem'];
}

// 给单品管理添加商品使用的商品选择抽屉 (可通用)
export const ItemSelectV5Drawer = (props: ItemSelectV5DrawerProps) => {
  const { t } = useTranslate();
  const {
    className,
    visible,
    onClose,
    renderTitleExtra,
    renderFooter,
    renderExtra,
    showSpuClassSelect = false,
    showSameSellerSkuTag = true,
    defaultParams,
    bindItem,
    ...restProps
  } = props;

  return (
    <SearchProvider defaultParams={defaultParams}>
      <BaseContextProvider column={2}>
        <ItemContextProvider bindItem={bindItem}>
          {(items, resetItems) => {
            return (
              <Drawer
                title={t('aide.添加商品')}
                placement="right"
                width={1280}
                maskClosable
                visible={visible}
                onClose={onClose}
                extra={renderTitleExtra ? renderTitleExtra(items, resetItems) : null}
                footer={renderFooter ? renderFooter(items, resetItems) : null}
                className={cx(
                  css`
                    .ant-drawer-body {
                      padding: 16px 16px 0 16px;
                      background: #f8f9fa;
                    }
                  `,
                  className,
                )}
                {...restProps}
              >
                <div className="h-full flex flex-col overflow-hidden gap-[16px]">
                  <SearchComp showSpuClassSelect={showSpuClassSelect} />
                  <div className="flex-grow flex flex-col overflow-hidden bg-[#fff]">
                    <ListView showSameSellerSkuTag={showSameSellerSkuTag} />
                    <div className="h-[54px] flex-shrink-0 flex justify-end items-center pr-[16px]">
                      <ListPagination />
                    </div>
                  </div>
                </div>
                {renderExtra ? renderExtra(items, resetItems) : null}
              </Drawer>
            );
          }}
        </ItemContextProvider>
      </BaseContextProvider>
    </SearchProvider>
  );
};

export interface ItemBindSingleClassDrawerSelfProps {
  onRefresh: () => void;
}
export interface ItemBindSingleClassDrawerProps
  extends ItemBindSingleClassDrawerSelfProps,
    DrawerProps {}
// 绑定未配置的商品到单品（非通用）
export const ItemBindSingleClassDrawer = (props: ItemBindSingleClassDrawerProps) => {
  const { t } = useTranslate();
  const { className, visible, onClose, ...restProps } = props;
  const [activeSpuClass, setActiveSpuClass] = useState<Knowledge.SingleClass.Value>();
  const lang = useSystemLanguageValue();
  const { mutateAsync, isLoading } = useUpdateSingleClass();
  const spuClassSelectRef = useRef<RefProps>(null);
  const queryClient = useQueryClient();

  return (
    <SearchProvider>
      <BaseContextProvider column={1}>
        <ItemContextProvider>
          {(items, resetItems) => {
            const skuLength = countSku(items, 'skuInfo');
            return (
              <Drawer
                title={t('aide.添加商品')}
                placement="right"
                width={1280}
                maskClosable
                visible={visible}
                onClose={onClose}
                footer={
                  <div className="flex items-center justify-end">
                    {activeSpuClass && !!skuLength && (
                      <span className="text-[#515359] mr-[12px]">
                        {t('已选{{X}}SKU添加至{{name}}', {
                          X: skuLength,
                          name: getMultiLangValue(
                            activeSpuClass?.nameMultiLang,
                            lang,
                            activeSpuClass?.name,
                          ),
                        })}
                      </span>
                    )}
                    <Button
                      disabled={skuLength === 0 || !activeSpuClass}
                      type="primary"
                      loading={isLoading}
                      onClick={() => {
                        mutateAsync({
                          singleClass: activeSpuClass!,
                          params: { items },
                        })
                          .then(() => {
                            props?.onRefresh();
                            message.success(t('已添加'));
                            resetItems();
                            queryClient.invalidateQueries(['getCategory']);
                            spuClassSelectRef.current?.refetch();
                          })
                          .catch(() => {
                            message.error(t('translation.操作失败，请稍后再试'));
                          });
                      }}
                    >
                      {t('department.确认添加')}
                    </Button>
                  </div>
                }
                className={cx(
                  css`
                    .ant-drawer-body {
                      padding: 16px 16px 0 16px;
                      background: #f8f9fa;
                    }
                  `,
                  className,
                )}
                {...restProps}
              >
                <div className="h-full flex flex-col overflow-hidden gap-[16px]">
                  <SearchComp />
                  <div className="flex-grow flex overflow-hidden bg-[#fff]">
                    <div className="flex-grow overflow-hidden flex flex-col">
                      <ListView />
                      <div className="h-[54px] flex-shrink-0 flex justify-end items-center pr-[16px]">
                        <ListPagination />
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-[317px] border-0 border-l border-solid border-[#f0f2f5]">
                      <SpuClassListSelect
                        ref={spuClassSelectRef}
                        value={activeSpuClass}
                        onChange={(value) => {
                          setActiveSpuClass(value);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Drawer>
            );
          }}
        </ItemContextProvider>
      </BaseContextProvider>
    </SearchProvider>
  );
};

ItemBindSingleClassDrawer.displayName = 'ItemBindSingleClassDrawer';
